import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  customerPastOrdersAsync,
  searchCustomerAsync,
} from "../../redux/features/customer/customerThunks";
import { CreateAccount } from "../../Screens/registerAccount";
import {
  selectIsUserSearched,
  selectPastOrders,
  selectSearchedCustomer,
  selectSelectedAddress,
  selectServiceType,
} from "../../redux/features/customer/customerSelectors";
import { AddressSelection } from "../../Screens/addressSelection";
import { transformOrders } from "../../utils/orders";
import { AvailableBrands } from "../../components/availableBrands";
import PastOrders from "../pastOrders";
import { fetchAreas } from "../../redux/features/country/countryThunks";
import { selectAreas } from "../../redux/features/country/countrySelectors";
import {
  selectBrandsList,
  selectPickupBrands,
} from "../../redux/features/restaurants/restaurantSelectors";
import { PickupOrder } from "../../Screens/pickupOrder";
import {
  setIsUserSearched,
  setServiceType,
  clearSearchedCustomer,
  setPastOrders,
  setSelectedAddress,
} from "../../redux/features/customer/customerSlice";
import {
  getAllPickupRestaurantsAsync,
  setDeliveryChargesThunk,
} from "../../redux/features/restaurants/restaurantThunks";
import AddNewAddress from "../../components/addAddress/addNewAddress";
import EditUserModal from "./EditUserModal";

// Validation schema using Yup
const validationSchema = Yup.object({
  phoneNumber: Yup.string()
    .matches(/^\d{8}$/, "Phone number must be exactly 8 digits") // Check for exactly 8 digits after +965
    .required("Phone number is required"),
});

export const UserSearch = ({ isCheckout = false, setShowAddressForm }) => {
  // Get customer data from Redux state
  const customerData = useSelector(selectSearchedCustomer);
  const isUserSearched = useSelector(selectIsUserSearched);
  const pastOrders = useSelector(selectPastOrders);
  const areas = useSelector(selectAreas);
  const branchesData = useSelector(selectBrandsList);
  const pickupBrands = useSelector(selectPickupBrands);
  const selectedAddress = useSelector(selectSelectedAddress);
  const serviceType = useSelector(selectServiceType);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [createNewAccount, setCreateNewAccount] = useState(false);
  const [showAddresses, setShowAddresses] = useState(false);
  const [showAvailableBrands, setAvailableBrands] = useState(false);
  const [userMobileNo, setUserMobileNo] = useState(false);
  const [pastOrdersList, setPastOrdersList] = useState();
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);

  const dispatch = useDispatch();

  // Handler for successful account creation
  const handleAccountCreated = () => {
    setCreateNewAccount(false);
    // Re-search for the user to load their new account data
    if (userMobileNo) {
      dispatch(searchCustomerAsync("+965", userMobileNo));
    }
  };

  // Handle clearing customer data when X button is clicked
  const handleClearCustomerData = () => {
    // Reset form fields
    formik.resetForm();
    
    // Clear all customer-related data in Redux
    dispatch(clearSearchedCustomer());
    dispatch(setPastOrders(null));
    dispatch(setSelectedAddress(null));
    if (setShowAddressForm) {
      setShowAddressForm(false); // Close the address form if it's open
    }
    
    // Reset local state
    setShowAddresses(false);
    setAvailableBrands(false);
    setPastOrdersList(null);
    setCreateNewAccount(false);
  };

  // Formik setup
  const formik = useFormik({
    initialValues: {
      phoneNumber: "",
      firstName: "",
      lastName: "",
      serviceType: serviceType,
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        // Dispatch the search action with the Kuwait phone number
        setUserMobileNo(values.phoneNumber);
        await dispatch(searchCustomerAsync("+965", values.phoneNumber, serviceType));
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    // Show CreateAccount if customerData is empty
    if (isUserSearched) {
      setCreateNewAccount(!customerData);
      // Show addresses if they exist
      setShowAddresses(customerData);
    }
  }, [dispatch, isUserSearched, customerData]);

  useEffect(() => {
    // Dispatch thunk only if customerData is available
    if (customerData?.id) {
      dispatch(customerPastOrdersAsync(customerData.id));
    }
  }, [customerData, dispatch]);

  useEffect(() => {
    if (pastOrders) {
      setPastOrdersList(transformOrders(pastOrders));
    }
  }, [pastOrders]);

  useEffect(() => {
    if (customerData) {
      formik.setValues({
        phoneNumber: customerData.mobile_no || "",
        firstName: customerData.first_name || "",
        lastName: customerData.last_name || "",
        serviceType: customerData.service_type || "delivery",
      });

      dispatch(setIsUserSearched(true));
    }
  }, [customerData]);

  useEffect(() => {
    if (!areas || areas.length === 0) {
      dispatch(fetchAreas());
    }
  }, [dispatch, areas]);

  useEffect(() => {
    if (branchesData && selectedAddress) {
      setAvailableBrands(true);
    } else {
      setAvailableBrands(false);
    }
  }, [branchesData, selectedAddress]);

  useEffect(() => {
    // On mount, set Formik and Redux serviceType based on Redux value
    if (serviceType) {
      formik.setFieldValue("serviceType", serviceType);
      dispatch(setDeliveryChargesThunk(serviceType));
      
      // If pickup, fetch pickup brands
      if (serviceType === "pickup" && !pickupBrands) {
        dispatch(getAllPickupRestaurantsAsync());
      }
      
      setInitializing(false);
    } else {
      // If no serviceType in redux, set default and finish initializing
      setInitializing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceType, pickupBrands]);

  // Filter pastOrdersList for delivery orders based on selected address
  const filteredDeliveryOrders =
    pastOrdersList &&
    (selectedAddress
      ? pastOrdersList?.filter(
          (order) =>
            order.service_type === "delivery" 
          // &&
          //   order.address_id === selectedAddress.id
        )
      : pastOrdersList?.filter((order) => order.service_type === "delivery"));

  // Don't show anything while initializing
  if (initializing) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4 mb-3 px-4 mt-4">
      <form onSubmit={formik.handleSubmit}>
        <div className="flex flex-col sm:flex-row sm:space-x-2 items-center">
          {/* Country Code */}
          <div className="mb-2 sm:mb-0">
            <input
              type="text"
              placeholder="+965 (KW)"
              readOnly
              className="border border-slate-300 bg-neutral-50 focus:ring-1 focus:ring-slate-300 focus:outline-none placeholder-gray-400 placeholder-bold px-4 py-2 w-full sm:w-[133px] h-12 rounded-xl"
            />
          </div>

          {/* Phone Number */}
          <div className="relative mb-2 sm:mb-0 w-full sm:w-[167px]">
            <input
              type="text"
              name="phoneNumber"
              value={formik.values.phoneNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={isCheckout}
              placeholder="Enter Phone Number"
              className="border border-slate-300 bg-neutral-50 rounded-xl focus:ring-1 focus:ring-slate-300 focus:outline-none placeholder-gray-400 placeholder-bold text-gray-400 font-bold px-4 py-2 w-full sm:w-[167px] h-12"
            />
            {/* X icon to clear input */}
            {formik.values.phoneNumber && !isCheckout && (
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={handleClearCustomerData}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
            {/* Error message */}
            {formik.touched.phoneNumber && formik.errors.phoneNumber && (
              <div className="absolute text-red-500 text-sm mt-1 left-0">
                {formik.errors.phoneNumber}
              </div>
            )}
          </div>

          {/* Search Button */}
          <div className="flex space-x-2">
            {!isCheckout && (
              <button
                type="submit"
                className="text-white font-bold bg-amber-800 rounded-xl h-12 w-[103px] focus:ring-1 focus:ring-amber-800 focus:outline-none"
                disabled={loading} // Disable button when loading
              >
                {loading ? "Searching..." : "Search"}
              </button>
            )}

            {/* Add New Address Button */}
            {!isCheckout && showAddresses && <AddNewAddress type="address" setShowAddressForm={setShowAddressForm} />}
          </div>
        </div>
      </form>
      {createNewAccount ? (
        <div className="mt-9">
          <CreateAccount
            mobileNumber={userMobileNo}
            onAccountCreated={handleAccountCreated}
          />
        </div>
      ) : (
        isUserSearched && (
          <div className="mt-7 flex flex-wrap space-x-2 items-center">
            <input
              type="text"
              name="firstName"
              value={formik.values.firstName}
              readOnly
              className="border border-slate-300 bg-neutral-50 rounded-xl focus:ring-1 focus:ring-slate-300 focus:outline-none text-gray-400 font-bold px-4 py-2 w-full sm:w-[167px] h-12"
            />
            <input
              type="text"
              name="lastName"
              value={formik.values.lastName}
              readOnly
              className="border border-slate-300 bg-neutral-50 rounded-xl focus:ring-1 focus:ring-slate-300 focus:outline-none text-gray-400 font-bold px-4 py-2 w-full sm:w-[167px] h-12"
            />
            {/* Edit User Button - Add before the radio buttons */}
            {!isCheckout && (
              <button
                type="button"
                onClick={() => setIsEditUserModalOpen(true)}
                className="text-white font-bold bg-amber-800 rounded-xl h-12 w-[103px] focus:ring-1 focus:ring-amber-800 focus:outline-none"
              >
                Edit User
              </button>
            )}
          </div>
        )
      )}

      {/* EditUserModal Component */}
      {customerData && (
        <EditUserModal
          isOpen={isEditUserModalOpen}
          onClose={() => setIsEditUserModalOpen(false)}
          userData={customerData}
        />
      )}

      {/* Display selected address in checkout */}
      {isCheckout && selectedAddress && formik.values.serviceType === "delivery" && (
        <div className="mt-4">
          <h2 className="text-lg font-bold text-gray-500 mb-2">Delivery Address</h2>
          <div className="min-w-[257px] max-w-[257px] w-[257px] h-[162px] border-2 border-amber-100 bg-neutral-50 p-4 rounded-xl flex flex-col justify-between">
            <div className="flex justify-between items-center">
              <h3 className="font-bold text-lg text-gray-500 truncate overflow-hidden">{selectedAddress.firstLine}</h3>
              {selectedAddress.active_order === "true" && (
                <button className="w-20 h-5 bg-emerald-600 text-white border border-white rounded-lg font-bold text-xs flex-shrink-0 ml-1">
                  Active Order
                </button>
              )}
            </div>
            <h4 className="text-gray-500 text-sm truncate">{selectedAddress.area}</h4>
            {selectedAddress.houseName && <h4 className="text-gray-500 text-sm truncate">{selectedAddress.houseName}</h4>}
            {selectedAddress.buildingNumber && <h5 className="text-gray-500 text-sm truncate">{selectedAddress.buildingNumber}</h5>}
            {selectedAddress.floor && <h6 className="text-gray-500 text-sm truncate">{selectedAddress.floor}</h6>}
            {selectedAddress.number && <p className="text-gray-500 text-sm truncate">{selectedAddress.number}</p>}
          </div>
        </div>
      )}

      {/* Only show these components if not in checkout */}
      {!isCheckout &&
        formik.values.serviceType === "delivery" &&
        customerData && (
          <div className="w-full flex gap-4 mt-4">
            {/* Left side: Address Selection and Past Orders - 50% width */}
            <div className="w-1/2 flex-shrink-0 space-y-4">
              {/* Address Selection */}
              <div>
                <AddressSelection isCheckout={isCheckout} setShowAddressForm={setShowAddressForm} />
              </div>

              {/* Past Orders - below address selection */}
              {filteredDeliveryOrders?.length > 0 && (
                <div>
                  <PastOrders pastOrdersList={filteredDeliveryOrders} />
                </div>
              )}
            </div>

            {/* Right side: Available Brands - 50% width - only show when address is selected and brands are available */}
            {showAvailableBrands && (
              <div className="w-1/2 flex-shrink-0">
                <AvailableBrands branchesData={branchesData} />
              </div>
            )}
          </div>
        )}

      {formik.values.serviceType === "pickup" && customerData && (
        <PickupOrder
          pastOrdersList={pastOrdersList}
          branchesData={pickupBrands}
          isCheckout={isCheckout}
        />
      )}
    </div>
  );
};
